import { computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { RealtimeService } from '../../../core/services/realtime.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  FILTER_STORAGE_KEYS,
  FilterSelectionStorageService,
} from '../../../core/services/filter-selection-storage.service';
import { EntretienApiService } from '../../../domain/api/entretien-api.service';
import {
  Entretien,
  EntretienEvent,
  EntretienFilterOptions,
  EntretienFilters,
  EntretienStatut,
  EntretienTotals,
} from '../../../domain/models';
import { reponseStatus } from './entretiens.format';

export type EntretienTab = 'liste' | 'calendrier';

const EMPTY_TOTALS: EntretienTotals = {
  total: 0,
  enAttente: 0,
  acceptes: 0,
  refuses: 0,
  reports: 0,
};

const EMPTY_OPTIONS: EntretienFilterOptions = {
  entreprises: [],
  apprenants: [],
  conseillers: [],
};

interface EntretiensState {
  entretiens: Entretien[];
  totals: EntretienTotals;
  page: number;
  limit: number;
  total: number;
  filters: EntretienFilters;
  options: EntretienFilterOptions;
  loading: boolean;
  initialLoad: boolean;
  activeTab: EntretienTab;
  selectedId: string | null;
  relancingId: string | null;
  updatingStatutId: string | null;
}

const initialState: EntretiensState = {
  entretiens: [],
  totals: EMPTY_TOTALS,
  page: 0,
  limit: 10,
  total: 0,
  filters: {},
  options: EMPTY_OPTIONS,
  loading: true,
  initialLoad: true,
  activeTab: 'liste',
  selectedId: null,
  relancingId: null,
  updatingStatutId: null,
};

export const EntretiensStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ entretiens, activeTab, selectedId, totals }) => {
    const confirmes = computed(() =>
      entretiens().filter((e) => reponseStatus(e) === 'confirmed'),
    );
    return {
      confirmes,
      enCoursCount: computed(() => totals().enAttente),
      totalCount: computed(() => totals().total),
      confirmesCount: computed(() => totals().acceptes),
      selected: computed(
        () => entretiens().find((e) => e.id === selectedId()) ?? null,
      ),
      showList: computed(() => activeTab() === 'liste'),
      showCalendar: computed(() => activeTab() === 'calendrier'),
    };
  }),
  withMethods((
    store,
    api = inject(EntretienApiService),
    toast = inject(ToastService),
    filterStorage = inject(FilterSelectionStorageService),
  ) => ({
    load(page = store.page(), limit = store.limit()): void {
      patchState(store, { loading: true });
      api
        .list({
          page,
          limit,
          ...store.filters(),
        })
        .subscribe({
          next: (response) => {
            patchState(store, {
              entretiens: response.entretiens,
              total: response.total,
              page: response.page,
              limit: response.limit,
              totals: response.totals,
              loading: false,
              initialLoad: false,
            });
          },
          error: () => {
            toast.error('Échec du chargement des entretiens.');
            patchState(store, { loading: false, initialLoad: false });
          },
        });
    },
    loadFilterOptions(): void {
      api.filterOptions().subscribe({
        next: (options) => patchState(store, { options }),
        error: () => {},
      });
    },
    refreshTotals(): void {
      api.list({ page: 0, limit: 1, ...store.filters() }).subscribe({
        next: (response) => patchState(store, { totals: response.totals }),
        error: () => {},
      });
    },
    setTab(tab: EntretienTab): void {
      patchState(store, { activeTab: tab, selectedId: null });
    },
    select(id: string): void {
      patchState(store, { selectedId: id });
    },
    closePanel(): void {
      patchState(store, { selectedId: null });
    },
    setFilters(filters: EntretienFilters): void {
      if (filters.conseillerIds !== undefined) {
        filterStorage.writeIds(
          FILTER_STORAGE_KEYS.entretiensConseillers,
          filters.conseillerIds,
        );
      }
      const next = { ...filters };
      if (next.conseillerIds?.length) {
        delete next.conseillerId;
      }
      patchState(store, { filters: next, page: 0, selectedId: null });
      this.load(0, store.limit());
    },
    updateFilters(partial: Partial<EntretienFilters>): void {
      const merged = { ...store.filters(), ...partial };
      if (partial.conseillerIds !== undefined) {
        delete merged.conseillerId;
      }
      this.setFilters(merged);
    },
    filterSansReponse(): void {
      this.updateFilters({ reponse: 'en_attente' });
    },
    resetFilters(): void {
      filterStorage.writeIds(FILTER_STORAGE_KEYS.entretiensConseillers, []);
      this.setFilters({});
    },
    setPage(page: number): void {
      patchState(store, { page, selectedId: null });
      this.load(page, store.limit());
    },
    setLimit(limit: number): void {
      patchState(store, { limit, page: 0, selectedId: null });
      this.load(0, limit);
    },
    applyEvent(_event: EntretienEvent): void {
      this.load(store.page(), store.limit());
      this.refreshTotals();
    },
    relancer(id: string): void {
      if (store.relancingId()) {
        return;
      }
      patchState(store, { relancingId: id });
      api.relancer(id).subscribe({
        next: (res) => {
          patchState(store, { relancingId: null });
          toast.success(`Relance envoyée à ${res.sentTo || 'l\'apprenant'}.`);
        },
        error: () => {
          patchState(store, { relancingId: null });
          toast.error('Impossible d\'envoyer la relance.');
        },
      });
    },
    updateStatut(
      id: string,
      payload: {
        statut: EntretienStatut;
        newDate?: string;
        newHeure?: string;
        reason?: string;
      },
    ): void {
      if (store.updatingStatutId()) {
        return;
      }
      patchState(store, { updatingStatutId: id });
      api.updateStatut(id, payload).subscribe({
        next: (updated) => {
          patchState(store, {
            updatingStatutId: null,
            entretiens: store.entretiens().map((e) => (e.id === id ? updated : e)),
          });
          toast.success('Statut mis à jour.');
          this.refreshTotals();
        },
        error: () => {
          patchState(store, { updatingStatutId: null });
          toast.error('Impossible de mettre à jour le statut.');
        },
      });
    },
  })),
  withHooks({
    onInit(
      store,
      realtime = inject(RealtimeService),
      filterStorage = inject(FilterSelectionStorageService),
    ) {
      const saved = filterStorage.readIds(FILTER_STORAGE_KEYS.entretiensConseillers);
      if (saved.length) {
        patchState(store, { filters: { conseillerIds: saved } });
      }
      store.load();
      store.loadFilterOptions();
      realtime
        .on<EntretienEvent>('entretien:event')
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.applyEvent(event));
    },
  }),
);
